import type { JSX, ComponentProps } from 'react';
import { styled } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { baseMarkdownCss, type MarkdownTextSize } from '../../css/baseMarkdownCss';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';

// react-markdown doesn't support `className` so we need to wrap it in a div to apply styles
const StyledMarkdownWrapper = styled.div<{ $textSize: MarkdownTextSize }>`
  ${({ $textSize }) => baseMarkdownCss($textSize)}
`;

export type MarkdownProps = ComponentProps<typeof ReactMarkdown> & {
  /**
   * A string of markdown to render.
   */
  children: string;
  /**
   * Adjusts the type scale based on the font size of the `<p>` element.
   * Supports responsive values.
   */
  textSize?: MarkdownTextSize | ResponsiveObject<MarkdownTextSize>;
};

/**
 * This is a simple wrapper around `react-markdown` that applies some basic styles.
 */
export const Markdown = ({
  children,
  textSize = 'body2',
  ...otherProps
}: MarkdownProps): JSX.Element => {
  const responsiveTextSize = useResponsiveProp(textSize);

  return (
    <StyledMarkdownWrapper
      $textSize={responsiveTextSize}
      {...otherProps}
    >
      <ReactMarkdown>{children}</ReactMarkdown>
    </StyledMarkdownWrapper>
  );
};

Markdown.displayName = 'Markdown';
